import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, subDays, format, startOfWeek, startOfMonth, subWeeks, subMonths, addDays, addMonths } from "date-fns";

interface DayStat {
  date: string;
  visitors: number;
  pageviews: number;
}

interface HourStat {
  hour: string;
  pageviews: number;
}

interface EntryExitPage {
  entry: string;
  exit: string;
  count: number;
}

export interface AnalyticsData {
  activeNow: number;
  todayVisitors: number;
  todayPageviews: number;
  yesterdayVisitors: number;
  yesterdayPageviews: number;
  weekVisitors: number;
  lastWeekVisitors: number;
  monthVisitors: number;
  lastMonthVisitors: number;
  weeklyData: DayStat[];
  weeklyTrend: { label: string; visitors: number }[];
  monthlyTrend: { label: string; visitors: number }[];
  topPages: { path: string; count: number }[];
  topReferrers: { referrer: string; count: number }[];
  deviceBreakdown: { device: string; count: number }[];
  // New metrics
  bounceRate: number; // percentage
  avgSessionDuration: number; // seconds
  
  peakHours: HourStat[];
  popularShelters: { name: string; path: string; count: number; lastOpen?: string; verified?: boolean }[];
  leastPopularShelters: { name: string; count: number; verified?: boolean }[];
  entryExitPages: EntryExitPage[];
  cityHeatmap: { city: string; count: number }[];
  buttonClicks: { name: string; count: number }[];
  recentlyOpenedShelters: { name: string; city: string; time: string; verified?: boolean }[];
  shelterOpenSources: { source: string; count: number }[];
  contactClicks: { type: string; count: number }[];
  emptySearches: { query: string; count: number }[];
  emptySearchesByVisitor: { label: string; queries: { query: string; time: string }[] }[];
  abandonedForms: number;
  submittedForms: number;
  formsByShelter: { name: string; opened: number; submitted: number }[];
  abandonedFormsList: { name: string; time: string; type: string }[];
  // Today-only metrics
  todayAvgSessionDuration: number;
  todayReferrers: { referrer: string; count: number }[];
  todayDeviceBreakdown: { device: string; count: number }[];
  todayPopularShelters: { name: string; count: number; verified?: boolean; times: string[] }[];
  todayEmptySearchesByVisitor: { label: string; queries: { query: string; time: string }[] }[];
  todayKeyPages: { label: string; path: string; visitors: number; pageviews: number }[];
  // Today-only conversion metrics
  todayShelterOpenSources: { source: string; count: number }[];
  todayContactClicks: { type: string; count: number }[];
  todayButtonClicks: { name: string; count: number }[];
  todayCityHeatmap: { city: string; count: number }[];
  todayEntryExitPages: EntryExitPage[];
  todaySubmittedForms: number;
  todayAbandonedForms: number;
  todayFormsByShelter: { name: string; opened: number; submitted: number }[];
  todayAbandonedFormsList: { name: string; time: string; type: string }[];
  // Share tracking (last 30 days)
  shareStats: {
    totalCopies: number;
    totalShareVisits: number;
    perShelter: { shelter_id: string | null; name: string; city: string; copies: number; visits: number }[];
  };
  // Help chat widget stats (last 30 days)
  chatStats: {
    opens: number;
    submits: number;
    categories: { key: string; label: string; count: number }[];
    subcategories: { subcategory: string; count: number }[];
  };
  // Help chat widget stats - today only
  todayChatStats: {
    opens: number;
    submits: number;
    categories: { key: string; label: string; count: number }[];
    subcategories: { subcategory: string; count: number }[];
  };
  // Bot/automated traffic excluded from stats
  botStats: {
    today: number;
    week: number;
    byPattern: { pattern: string; today: number; week: number }[];
  };
}

const detectDevice = (ua: string): string => {
  if (/mobile|android|iphone|ipod/i.test(ua)) return "Telefon";
  if (/tablet|ipad/i.test(ua)) return "Tablet";
  return "Komputer";
};

// Wzorce wykluczonego ruchu - kolejność ma znaczenie (pierwsze trafienie wygrywa).
export const BOT_UA_PATTERNS: { label: string; test: (ua: string) => boolean }[] = [
  { label: "Lighthouse / PageSpeed", test: (ua) => /lighthouse|pagespeed/i.test(ua) },
  { label: "Google Search Console", test: (ua) => /google-inspectiontool|google page speed/i.test(ua) },
  { label: "Lighthouse mobile (Moto G Power)", test: (ua) => /moto g power \(2022\)/i.test(ua) },
  { label: "Search bots", test: (ua) => /googlebot|bingbot|yandex|baidu|duckduck|slurp/i.test(ua) },
  { label: "Social bots", test: (ua) => /facebookexternalhit|twitterbot|linkedinbot/i.test(ua) },
  { label: "AI crawlers", test: (ua) => /gptbot|claudebot|bytespider|petalbot/i.test(ua) },
  { label: "SEO crawlers", test: (ua) => /semrush|ahrefs|mj12bot|dotbot/i.test(ua) },
  { label: "Inne boty", test: (ua) => /bot|crawl|spider/i.test(ua) },
  { label: "Headless / automation", test: (ua) => /headless|phantomjs|puppeteer|playwright/i.test(ua) },
  {
    label: "Stary Chrome (<120, prawdop. Lighthouse)",
    test: (ua) => {
      const m = ua.match(/Chrome\/(\d+)/);
      return !!m && parseInt(m[1], 10) < 120;
    },
  },
];

const matchBotPattern = (ua: string): string | null => {
  if (!ua) return null;
  for (const p of BOT_UA_PATTERNS) {
    if (p.test(ua)) return p.label;
  }
  return null;
};

const isBot = (ua: string): boolean => matchBotPattern(ua) !== null;

export const useAnalytics = () => {
  return useQuery({
    queryKey: ["analytics"],
    queryFn: async (): Promise<AnalyticsData> => {
      const now = new Date();
      const todayStart = startOfDay(now);
      const yesterdayStart = startOfDay(subDays(now, 1));
      const weekAgo = startOfDay(subDays(now, 6));
      // Trends: data is auto-purged after 90 days, so we fetch up to that
      const trendStart = startOfDay(subDays(now, 89));

      // Active visitors (last_seen within 30 minutes), excluding admin IDs
      const thirtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { count: activeNow } = await supabase
        .from("active_visitors")
        .select("*", { count: "exact", head: true })
        .gte("last_seen", thirtyMinAgo)
        .not("visitor_id", "in", '("eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b","d6d0ccd7-275c-487b-a4d8-1e5015648d1e","980d77c6-80bb-4f59-ae95-ce346571f976","a5395a39-8b3b-4135-b821-ab7db342badc","8c76f383-0a15-4bbd-a17d-0474169ea076","d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78")');

      // Fetch all visits from last 60 days (needed for week/month vs prev comparisons)
      const { data: visits, error } = await supabase
        .from("page_visits")
        .select("*")
        .gte("created_at", trendStart.toISOString())
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Filter out lovable.dev traffic, admin visitors, and lovableproject.com referrers
      const EXCLUDED_VISITOR_IDS = [
        "eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b",
        "d6d0ccd7-275c-487b-a4d8-1e5015648d1e",
        "980d77c6-80bb-4f59-ae95-ce346571f976",
        "a5395a39-8b3b-4135-b821-ab7db342badc",
        "8c76f383-0a15-4bbd-a17d-0474169ea076",
        "d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78",
      ];
      const EXCLUDED_REFERRERS = ["lovable.dev", "lovableproject.com"];

      // Identify all visitors who ever accessed /admin — they are admins, exclude their entire activity
      const adminVisitorIds = new Set<string>(EXCLUDED_VISITOR_IDS);
      (visits || []).forEach(r => {
        if (r.path && r.path.startsWith("/admin")) adminVisitorIds.add(r.visitor_id);
      });

      const allRows = (visits || []).filter(r => {
        if (adminVisitorIds.has(r.visitor_id)) return false;
        if (r.referrer && EXCLUDED_REFERRERS.some(ref => r.referrer!.includes(ref))) return false;
        // Wykluczamy boty: najpierw po fladze z DB (nowe wpisy), a w razie braku po UA (wstecz kompatybilność).
        if ((r as { is_bot?: boolean }).is_bot === true) return false;
        if (isBot(r.user_agent || "")) return false;
        return true;
      });
      // Most existing computations operate on the last 7 days only
      const rows = allRows.filter(r => new Date(r.created_at) >= weekAgo);

      // Calendar week (Mon-based) — current vs previous full week
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const prevWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const weekVisitorsSet = new Set<string>();
      const lastWeekVisitorsSet = new Set<string>();
      // Calendar month — current vs previous full month
      const monthStart = startOfMonth(now);
      const prevMonthStart = startOfMonth(subMonths(now, 1));
      const monthVisitorsSet = new Set<string>();
      const lastMonthVisitorsSet = new Set<string>();
      allRows.forEach(r => {
        const d = new Date(r.created_at);
        if (d >= weekStart) weekVisitorsSet.add(r.visitor_id);
        else if (d >= prevWeekStart) lastWeekVisitorsSet.add(r.visitor_id);
        if (d >= monthStart) monthVisitorsSet.add(r.visitor_id);
        else if (d >= prevMonthStart) lastMonthVisitorsSet.add(r.visitor_id);
      });
      const weekVisitors = weekVisitorsSet.size;
      const lastWeekVisitors = lastWeekVisitorsSet.size;
      const monthVisitors = monthVisitorsSet.size;
      const lastMonthVisitors = lastMonthVisitorsSet.size;

      // Weekly trend — last 12 calendar weeks (Mon-based), unique visitors per week
      const weeklyTrend: { label: string; visitors: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const wStart = startOfWeek(subWeeks(now, i), { weekStartsOn: 1 });
        const wEnd = addDays(wStart, 7);
        const set = new Set<string>();
        allRows.forEach(r => {
          const d = new Date(r.created_at);
          if (d >= wStart && d < wEnd) set.add(r.visitor_id);
        });
        weeklyTrend.push({ label: format(wStart, "dd.MM"), visitors: set.size });
      }

      // Monthly trend — last 6 calendar months
      const MONTH_LABELS = ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"];
      const monthlyTrend: { label: string; visitors: number }[] = [];
      for (let i = 5; i >= 0; i--) {
        const mStart = startOfMonth(subMonths(now, i));
        const mEnd = addMonths(mStart, 1);
        const set = new Set<string>();
        allRows.forEach(r => {
          const d = new Date(r.created_at);
          if (d >= mStart && d < mEnd) set.add(r.visitor_id);
        });
        monthlyTrend.push({ label: MONTH_LABELS[mStart.getMonth()], visitors: set.size });
      }

      // Fetch event-based metrics from last 30 days (popular shelters, conversions, forms)
      const thirtyDaysAgo = startOfDay(subDays(now, 29));
      const { data: events } = await (supabase.from("analytics_events") as any)
        .select("*")
        .gte("created_at", thirtyDaysAgo.toISOString())
        .not("visitor_id", "in", '("eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b","d6d0ccd7-275c-487b-a4d8-1e5015648d1e","980d77c6-80bb-4f59-ae95-ce346571f976","a5395a39-8b3b-4135-b821-ab7db342badc","8c76f383-0a15-4bbd-a17d-0474169ea076","d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78")');

      // Fetch ALL-TIME shelter open events (for least-popular ranking — show shelters never opened)
      const { data: allTimeShelterOpens } = await (supabase.from("analytics_events") as any)
        .select("event_data")
        .eq("event_name", "Otworzono schronisko")
        .not("visitor_id", "in", '("eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b","d6d0ccd7-275c-487b-a4d8-1e5015648d1e","980d77c6-80bb-4f59-ae95-ce346571f976","a5395a39-8b3b-4135-b821-ab7db342badc","8c76f383-0a15-4bbd-a17d-0474169ea076","d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78")');

      // Fetch shelters for city heatmap & popular shelters
      const { data: shelters } = await supabase
        .from("shelters")
        .select("id, name, city, organization_verified, slug");

      // Today
      const todayRows = rows.filter(r => new Date(r.created_at) >= todayStart);
      const todayVisitors = new Set(todayRows.map(r => r.visitor_id)).size;
      const todayPageviews = todayRows.length;

      // Today — avg session duration
      const todayVisitorTimes = new Map<string, { first: number; last: number }>();
      todayRows.forEach(r => {
        const t = new Date(r.created_at).getTime();
        const ex = todayVisitorTimes.get(r.visitor_id);
        if (!ex) todayVisitorTimes.set(r.visitor_id, { first: t, last: t });
        else { if (t < ex.first) ex.first = t; if (t > ex.last) ex.last = t; }
      });
      let todayTotalDuration = 0, todaySessions = 0;
      todayVisitorTimes.forEach(({ first, last }) => {
        const d = (last - first) / 1000;
        if (d > 0) { todayTotalDuration += d; todaySessions++; }
      });
      const todayAvgSessionDuration = todaySessions > 0 ? Math.round(todayTotalDuration / todaySessions) : 0;

      // Today — referrers (unique visitors per referrer)
      const todayRefVisitors: Record<string, Set<string>> = {};
      todayRows.forEach(r => {
        let ref = "Bezpośredni";
        if (r.referrer) {
          try { ref = new URL(r.referrer).hostname || "Bezpośredni"; } catch { ref = r.referrer; }
        }
        if (!todayRefVisitors[ref]) todayRefVisitors[ref] = new Set();
        todayRefVisitors[ref].add(r.visitor_id);
      });
      const todayReferrers = Object.entries(todayRefVisitors)
        .map(([referrer, vs]) => ({ referrer, count: vs.size }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Today — device breakdown
      const todayDeviceCounts: Record<string, number> = {};
      const todayUniqueUA = new Map<string, string>();
      todayRows.forEach(r => {
        if (!todayUniqueUA.has(r.visitor_id)) todayUniqueUA.set(r.visitor_id, r.user_agent || "");
      });
      todayUniqueUA.forEach(ua => {
        const d = detectDevice(ua);
        todayDeviceCounts[d] = (todayDeviceCounts[d] || 0) + 1;
      });
      const todayDeviceBreakdown = Object.entries(todayDeviceCounts)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count);

      // Today — kluczowe podstrony (formularze i broszura): unikalni odwiedzający i odsłony
      const KEY_PAGES: { label: string; match: (p: string) => boolean }[] = [
        { label: "Zgłoś aktualizację", match: (p) => p === "/zglos" || p.startsWith("/zglos/") || p.startsWith("/zglos?") },
        { label: "Szybka aktualizacja", match: (p) => p === "/szybka-aktualizacja" || p.startsWith("/szybka-aktualizacja/") || p.startsWith("/szybka-aktualizacja?") },
        { label: "Dla schronisk (broszura)", match: (p) => p === "/dla-schronisk" || p.startsWith("/dla-schronisk/") || p.startsWith("/dla-schronisk?") },
      ];
      const todayKeyPages = KEY_PAGES.map(({ label, match }) => {
        const matched = todayRows.filter((r) => match(r.path));
        return {
          label,
          path: matched[0]?.path ?? "",
          visitors: new Set(matched.map((r) => r.visitor_id)).size,
          pageviews: matched.length,
        };
      });

      // Yesterday
      const yesterdayRows = rows.filter(r => {
        const d = new Date(r.created_at);
        return d >= yesterdayStart && d < todayStart;
      });
      const yesterdayVisitors = new Set(yesterdayRows.map(r => r.visitor_id)).size;
      const yesterdayPageviews = yesterdayRows.length;

      // Weekly data
      const weeklyData: DayStat[] = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = startOfDay(subDays(now, i));
        const dayEnd = startOfDay(subDays(now, i - 1));
        const effectiveRows = i === 0
          ? rows.filter(r => new Date(r.created_at) >= dayStart)
          : rows.filter(r => {
              const d = new Date(r.created_at);
              return d >= dayStart && d < dayEnd;
            });
        weeklyData.push({
          date: format(dayStart, "dd.MM"),
          visitors: new Set(effectiveRows.map(r => r.visitor_id)).size,
          pageviews: effectiveRows.length,
        });
      }

      // Top pages
      const pageCounts: Record<string, number> = {};
      rows.forEach(r => { pageCounts[r.path] = (pageCounts[r.path] || 0) + 1; });
      const topPages = Object.entries(pageCounts)
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Top referrers — unique visitors per referrer (not pageviews)
      const refVisitors: Record<string, Set<string>> = {};
      rows.forEach(r => {
        let ref = "Bezpośredni";
        if (r.referrer) {
          try {
            ref = new URL(r.referrer).hostname || "Bezpośredni";
          } catch {
            ref = r.referrer;
          }
        }
        if (!refVisitors[ref]) refVisitors[ref] = new Set();
        refVisitors[ref].add(r.visitor_id);
      });
      const topReferrers = Object.entries(refVisitors)
        .map(([referrer, visitors]) => ({ referrer, count: visitors.size }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Device breakdown
      const deviceCounts: Record<string, number> = {};
      const uniqueVisitors = new Map<string, string>();
      rows.forEach(r => {
        if (!uniqueVisitors.has(r.visitor_id)) {
          uniqueVisitors.set(r.visitor_id, r.user_agent || "");
        }
      });
      uniqueVisitors.forEach(ua => {
        const device = detectDevice(ua);
        deviceCounts[device] = (deviceCounts[device] || 0) + 1;
      });
      const deviceBreakdown = Object.entries(deviceCounts)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count);

      // === NEW METRICS ===

      // 1. Bounce rate — visitors with only 1 pageview (7 days)
      const visitorPageCounts = new Map<string, number>();
      rows.forEach(r => {
        visitorPageCounts.set(r.visitor_id, (visitorPageCounts.get(r.visitor_id) || 0) + 1);
      });
      const totalVisitorsWeek = visitorPageCounts.size;
      const bouncedVisitors = Array.from(visitorPageCounts.values()).filter(c => c === 1).length;
      const bounceRate = totalVisitorsWeek > 0 ? Math.round((bouncedVisitors / totalVisitorsWeek) * 100) : 0;

      // 2. Average session duration (seconds) — diff between first and last visit per visitor (7 days)
      const visitorTimes = new Map<string, { first: number; last: number }>();
      rows.forEach(r => {
        const t = new Date(r.created_at).getTime();
        const existing = visitorTimes.get(r.visitor_id);
        if (!existing) {
          visitorTimes.set(r.visitor_id, { first: t, last: t });
        } else {
          if (t < existing.first) existing.first = t;
          if (t > existing.last) existing.last = t;
        }
      });
      let totalDuration = 0;
      let sessionsWithDuration = 0;
      visitorTimes.forEach(({ first, last }) => {
        const dur = (last - first) / 1000;
        if (dur > 0) {
          totalDuration += dur;
          sessionsWithDuration++;
        }
      });
      const avgSessionDuration = sessionsWithDuration > 0 ? Math.round(totalDuration / sessionsWithDuration) : 0;

      // 4. Peak hours — pageviews by hour of day (last 7 days)
      const hourCounts: Record<number, number> = {};
      rows.forEach(r => {
        const hour = new Date(r.created_at).getHours();
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      });
      const peakHours: HourStat[] = [];
      for (let h = 0; h < 24; h++) {
        peakHours.push({ hour: `${h.toString().padStart(2, "0")}:00`, pageviews: hourCounts[h] || 0 });
      }

      // 5. Popular shelters — sumujemy wejścia na profile (page_visits do /schronisko/<slug>),
      // co obejmuje zarówno kliknięcia z listy/mapy w aplikacji, jak i bezpośrednie wejścia z linków.
      const shelterMap = new Map((shelters || []).map(s => [s.id, s]));
      const slugToShelter = new Map(
        (shelters || [])
          .filter(s => s.slug)
          .map(s => [s.slug, s])
      );
      const shelterOpenEvents = (events || []).filter((e: any) => e.event_name === "Otworzono schronisko");

      const extractSlug = (path: string): string | null => {
        if (!path || !path.startsWith("/schronisko/")) return null;
        const rest = path.slice("/schronisko/".length);
        const slug = rest.split(/[/?#]/)[0];
        return slug || null;
      };

      // 30-dniowe wejścia na profile (z page_visits, już z odfiltrowanymi botami i adminami)
      const thirtyDayShelterVisits = allRows.filter(r => {
        const d = new Date(r.created_at);
        return d >= thirtyDaysAgo && extractSlug(r.path) !== null;
      });

      const shelterVisitCounts: Record<string, { name: string; count: number; lastOpen: string; verified?: boolean }> = {};
      thirtyDayShelterVisits.forEach(r => {
        const slug = extractSlug(r.path);
        if (!slug) return;
        const sh = slugToShelter.get(slug);
        if (!sh) return;
        if (!shelterVisitCounts[sh.id]) {
          shelterVisitCounts[sh.id] = {
            name: sh.name,
            count: 0,
            lastOpen: r.created_at,
            verified: !!(sh as any).organization_verified,
          };
        }
        shelterVisitCounts[sh.id].count++;
        if (new Date(r.created_at) > new Date(shelterVisitCounts[sh.id].lastOpen)) {
          shelterVisitCounts[sh.id].lastOpen = r.created_at;
        }
      });
      const popularShelters = Object.values(shelterVisitCounts)
        .map(s => ({ ...s, path: "" }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 30);

      // Today — popular shelters (na podstawie page_visits), sortowane od najnowszego otwarcia
      const todayShelterCounts: Record<string, { name: string; count: number; verified?: boolean; times: string[]; lastOpen: number }> = {};
      todayRows.forEach(r => {
        const slug = extractSlug(r.path);
        if (!slug) return;
        const sh = slugToShelter.get(slug);
        if (!sh) return;
        if (!todayShelterCounts[sh.id]) {
          todayShelterCounts[sh.id] = {
            name: sh.name,
            count: 0,
            verified: !!(sh as any).organization_verified,
            times: [],
            lastOpen: 0,
          };
        }
        const t = new Date(r.created_at);
        const ts = t.getTime();
        todayShelterCounts[sh.id].count++;
        todayShelterCounts[sh.id].times.push(
          `${t.getHours().toString().padStart(2, "0")}:${t.getMinutes().toString().padStart(2, "0")}`
        );
        if (ts > todayShelterCounts[sh.id].lastOpen) todayShelterCounts[sh.id].lastOpen = ts;
      });
      Object.values(todayShelterCounts).forEach(s => s.times.sort((a, b) => b.localeCompare(a)));
      const todayPopularShelters = Object.values(todayShelterCounts)
        .sort((a, b) => b.lastOpen - a.lastOpen)
        .map(({ lastOpen: _lo, ...rest }) => rest)
        .slice(0, 50);

      // Least popular — count opens ALL TIME so shelters with 0 opens ever surface
      const allTimeOpenCounts = new Map<string, number>();
      (allTimeShelterOpens || []).forEach((e: any) => {
        const id = e.event_data?.shelter_id;
        if (id) allTimeOpenCounts.set(id, (allTimeOpenCounts.get(id) || 0) + 1);
      });
      const allSheltersWithCounts = (shelters || []).map(s => ({
        name: s.name,
        count: allTimeOpenCounts.get(s.id) || 0,
        verified: !!(s as any).organization_verified,
      }));
      const leastPopularShelters = allSheltersWithCounts
        .sort((a, b) => a.count - b.count)
        .slice(0, 10);

      // 6. Entry → Exit pages per visitor
      const visitorPages = new Map<string, { entry: string; exit: string }>();
      rows.forEach(r => {
        if (!visitorPages.has(r.visitor_id)) {
          visitorPages.set(r.visitor_id, { entry: r.path, exit: r.path });
        } else {
          visitorPages.get(r.visitor_id)!.exit = r.path;
        }
      });
      const entryExitCounts = new Map<string, number>();
      visitorPages.forEach(({ entry, exit }) => {
        const key = `${entry}|||${exit}`;
        entryExitCounts.set(key, (entryExitCounts.get(key) || 0) + 1);
      });
      const entryExitPages: EntryExitPage[] = Array.from(entryExitCounts.entries())
        .map(([key, count]) => {
          const [entry, exit] = key.split("|||");
          return { entry, exit, count };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 7. City heatmap — based on "Otworzono schronisko" events
      const cityCounts: Record<string, number> = {};
      shelterOpenEvents.forEach((e: any) => {
        const city = e.event_data?.shelter_city;
        const shelterId = e.event_data?.shelter_id;
        if (city) {
          cityCounts[city] = (cityCounts[city] || 0) + 1;
        } else if (shelterId && shelterMap.has(shelterId)) {
          const shelter = shelterMap.get(shelterId)!;
          cityCounts[shelter.city] = (cityCounts[shelter.city] || 0) + 1;
        }
      });
      const cityHeatmap = Object.entries(cityCounts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 8. Button clicks — include filter details from event_data
      const clickCounts: Record<string, number> = {};
      (events || []).forEach((e: any) => {
        if (e.event_name) {
          let label = e.event_name;
          // For filter events, append the specific filter/value
          if (e.event_name === "Filtr" && e.event_data?.filter) {
            label = `Filtr: ${e.event_data.filter} → ${e.event_data.enabled ? "ON" : "OFF"}`;
          } else if (e.event_name === "Filtr miasto" && e.event_data?.city) {
            label = `Filtr miasto: ${e.event_data.city}`;
          }
          clickCounts[label] = (clickCounts[label] || 0) + 1;
        }
      });
      const buttonClicks = Object.entries(clickCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      // 9. Recently opened shelters — łączymy eventy "Otworzono schronisko" z bezpośrednimi wejściami na profile (page_visits)
      type RecentEntry = { name: string; city: string; time: string; verified?: boolean };
      const recentEntries: RecentEntry[] = [];
      shelterOpenEvents.forEach((e: any) => {
        const id = e.event_data?.shelter_id;
        const sh = id ? shelterMap.get(id) : null;
        const name = e.event_data?.shelter_name || sh?.name;
        if (!name) return;
        recentEntries.push({
          name,
          city: e.event_data?.shelter_city || sh?.city || "",
          time: e.created_at,
          verified: sh ? !!(sh as any).organization_verified : false,
        });
      });
      thirtyDayShelterVisits.forEach(r => {
        const slug = extractSlug(r.path);
        if (!slug) return;
        const sh = slugToShelter.get(slug);
        if (!sh) return;
        recentEntries.push({
          name: sh.name,
          city: sh.city || "",
          time: r.created_at,
          verified: !!(sh as any).organization_verified,
        });
      });
      recentEntries.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
      const seenShelters = new Set<string>();
      const recentlyOpenedShelters: RecentEntry[] = [];
      for (const entry of recentEntries) {
        if (seenShelters.has(entry.name)) continue;
        seenShelters.add(entry.name);
        recentlyOpenedShelters.push(entry);
        if (recentlyOpenedShelters.length >= 30) break;
      }

      // 10. Shelter open sources — breakdown by source (list/map/search)
      const sourceCounts: Record<string, number> = {};
      const SOURCE_LABELS: Record<string, string> = {
        list: "Lista",
        map: "Mapa",
        search: "Wyszukiwarka",
        unknown: "Nieznane",
      };
      shelterOpenEvents.forEach((e: any) => {
        const raw = e.event_data?.source || "unknown";
        const label = SOURCE_LABELS[raw] || raw;
        sourceCounts[label] = (sourceCounts[label] || 0) + 1;
      });
      const shelterOpenSources = Object.entries(sourceCounts)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      // 11. Contact clicks — Telefon, Email, Strona www
      const contactTypes = ["Telefon", "Email", "Strona www"];
      const contactClickCounts: Record<string, number> = {};
      (events || []).forEach((e: any) => {
        if (contactTypes.includes(e.event_name)) {
          contactClickCounts[e.event_name] = (contactClickCounts[e.event_name] || 0) + 1;
        }
      });
      const contactClicks = contactTypes
        .map(type => ({ type, count: contactClickCounts[type] || 0 }))
        .filter(c => c.count > 0);

      // 12. Empty searches — "Wyszukiwanie bez wyników"
      const emptySearchCounts: Record<string, number> = {};
      (events || []).filter((e: any) => e.event_name === "Wyszukiwanie bez wyników").forEach((e: any) => {
        const query = e.event_data?.query;
        if (query) {
          emptySearchCounts[query] = (emptySearchCounts[query] || 0) + 1;
        }
      });
      const emptySearches = Object.entries(emptySearchCounts)
        .map(([query, count]) => ({ query, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 50);

      // 30 dni — empty searches pogrupowane po osobach (1, 2, 3... od najnowszych)
      const allEmptySearchEvents = (events || [])
        .filter((e: any) => e.event_name === "Wyszukiwanie bez wyników")
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const allVisitorLabelMap = new Map<string, string>();
      const allLabelFor = (vid: string) => {
        if (!allVisitorLabelMap.has(vid)) {
          allVisitorLabelMap.set(vid, String(allVisitorLabelMap.size + 1));
        }
        return allVisitorLabelMap.get(vid)!;
      };
      const allVisitorBuckets = new Map<string, { label: string; queries: { query: string; time: string }[] }>();
      allEmptySearchEvents.forEach((e: any) => {
        const query = e.event_data?.query;
        if (!query) return;
        const vid = e.visitor_id || "anon";
        const label = allLabelFor(vid);
        const bucket = allVisitorBuckets.get(vid) ?? { label, queries: [] };
        bucket.queries.push({ query, time: format(new Date(e.created_at), "dd.MM HH:mm") });
        allVisitorBuckets.set(vid, bucket);
      });
      const emptySearchesByVisitor = Array.from(allVisitorBuckets.values())
        .sort((a, b) => Number(a.label) - Number(b.label));

      // Today — empty searches pogrupowane po osobach (1, 2, 3... od najnowszych)
      const todayEmptySearchEvents = (events || [])
        .filter((e: any) => e.event_name === "Wyszukiwanie bez wyników" && new Date(e.created_at) >= todayStart)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const visitorLabelMap = new Map<string, string>();
      const labelFor = (vid: string) => {
        if (!visitorLabelMap.has(vid)) {
          visitorLabelMap.set(vid, String(visitorLabelMap.size + 1));
        }
        return visitorLabelMap.get(vid)!;
      };
      const visitorBuckets = new Map<string, { label: string; queries: { query: string; time: string }[] }>();
      todayEmptySearchEvents.forEach((e: any) => {
        const query = e.event_data?.query;
        if (!query) return;
        const vid = e.visitor_id || "anon";
        const label = labelFor(vid);
        const bucket = visitorBuckets.get(vid) ?? { label, queries: [] };
        bucket.queries.push({ query, time: format(new Date(e.created_at), "HH:mm") });
        visitorBuckets.set(vid, bucket);
      });
      const todayEmptySearchesByVisitor = Array.from(visitorBuckets.values())
        .sort((a, b) => Number(a.label) - Number(b.label));

      // 13. Abandoned forms — deduplicate per visitor+shelter (React StrictMode fires effects twice)
      const formOpenEvents = (events || []).filter((e: any) => e.event_name === "Formularz otwarty");
      const formSubmitEvents = (events || []).filter((e: any) => e.event_name === "Formularz wysłany");
      
      // Deduplicate opens: count unique visitor+shelter+type combos (within 30s window)
      const dedupeKey = (e: any) => `${e.visitor_id}|${e.event_data?.shelter_id || 'new'}|${e.event_data?.type || ''}`;
      const seenFormOpens = new Map<string, any>();
      formOpenEvents.forEach((e: any) => {
        const key = dedupeKey(e);
        const existing = seenFormOpens.get(key);
        if (!existing) {
          seenFormOpens.set(key, e);
        }
        // Skip duplicates within 30s of first occurrence
      });
      const dedupedOpens = Array.from(seenFormOpens.values());
      
      const formOpens = dedupedOpens.length;
      const submittedForms = formSubmitEvents.length;
      const abandonedForms = Math.max(0, formOpens - submittedForms);

      // Per-shelter form stats (using deduped opens)
      // Dla zgłoszeń nowych schronisk (bez shelter_id) grupujemy pod kluczem "__new__"
      const formShelterOpens: Record<string, { name: string; opened: number; submitted: number }> = {};
      const NEW_KEY = "__new__";
      dedupedOpens.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        const key = sid || NEW_KEY;
        const name = sid
          ? (shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid))
          : "Nowe schronisko";
        if (!formShelterOpens[key]) formShelterOpens[key] = { name, opened: 0, submitted: 0 };
        formShelterOpens[key].opened++;
      });
      formSubmitEvents.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        const key = sid || NEW_KEY;
        const name = sid
          ? (shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid))
          : "Nowe schronisko";
        if (!formShelterOpens[key]) formShelterOpens[key] = { name, opened: 0, submitted: 0 };
        formShelterOpens[key].submitted++;
      });
      const formsByShelter = Object.values(formShelterOpens)
        .sort((a, b) => (b.opened + b.submitted) - (a.opened + a.submitted))
        .slice(0, 10);

      // Abandoned forms list with timestamps
      const submittedKeys = new Set(formSubmitEvents.map((e: any) => dedupeKey(e)));
      const abandonedFormsList = dedupedOpens
        .filter((e: any) => !submittedKeys.has(dedupeKey(e)))
        .map((e: any) => {
          const sid = e.event_data?.shelter_id;
          const name = sid && shelterMap.has(sid)
            ? shelterMap.get(sid)!.name
            : (e.event_data?.shelter_name || (e.event_data?.type === "nowe" ? "Nowe schronisko" : "—"));
          return { name, time: e.created_at, type: e.event_data?.type || "" };
        })
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 15);

      // 14. Share stats (last 30 days)
      type ShareAgg = { name: string; city: string; copies: number; visits: number };
      const shareAgg = new Map<string, ShareAgg>();
      let totalCopies = 0;
      let totalShareVisits = 0;
      (events || []).forEach((e: any) => {
        const isCopy = e.event_name === "Udostępnij - skopiowano link";
        const isVisit = e.event_name === "Profil - wejście z linku";
        if (!isCopy && !isVisit) return;
        const sid: string | null = e.event_data?.shelter_id || null;
        if (!sid) return;
        const fallbackName = e.event_data?.shelter_name || "—";
        const known = shelterMap.get(sid);
        const name = known?.name || fallbackName;
        const city = known?.city || "";
        const cur = shareAgg.get(sid) || { name, city, copies: 0, visits: 0 };
        if (isCopy) { cur.copies++; totalCopies++; }
        if (isVisit) { cur.visits++; totalShareVisits++; }
        shareAgg.set(sid, cur);
      });
      const shareStats = {
        totalCopies,
        totalShareVisits,
        perShelter: Array.from(shareAgg.entries())
          .map(([shelter_id, v]) => ({ shelter_id, ...v }))
          .sort((a, b) => (b.copies + b.visits) - (a.copies + a.visits))
          .slice(0, 10),
      };

      // 14b. Chat stats (last 30 days) - widget pomocy
      const CHAT_CAT_LABELS: Record<string, string> = {
        bug: "Zgłoś błąd",
        update: "Aktualizacja danych",
        idea: "Pomysł / sugestia",
        other: "Inne / współpraca",
      };
      let chatOpens = 0;
      let chatSubmits = 0;
      const chatCategories = new Map<string, number>();
      const chatSubcategories = new Map<string, number>();
      let todayChatOpens = 0;
      let todayChatSubmits = 0;
      const todayChatCategories = new Map<string, number>();
      const todayChatSubcategories = new Map<string, number>();
      (events || []).forEach((e: any) => {
        const isToday = new Date(e.created_at) >= todayStart;
        if (e.event_name === "Czat - otwarcie") {
          chatOpens++;
          if (isToday) todayChatOpens++;
        } else if (e.event_name === "Czat - kategoria") {
          const c = String(e.event_data?.category || "");
          if (c) {
            chatCategories.set(c, (chatCategories.get(c) || 0) + 1);
            if (isToday) todayChatCategories.set(c, (todayChatCategories.get(c) || 0) + 1);
          }
        } else if (e.event_name === "Czat - podkategoria") {
          const s = String(e.event_data?.subcategory || "");
          if (s) {
            chatSubcategories.set(s, (chatSubcategories.get(s) || 0) + 1);
            if (isToday) todayChatSubcategories.set(s, (todayChatSubcategories.get(s) || 0) + 1);
          }
        } else if (e.event_name === "Czat - wyslane") {
          chatSubmits++;
          if (isToday) todayChatSubmits++;
        }
      });
      const chatStats = {
        opens: chatOpens,
        submits: chatSubmits,
        categories: Array.from(chatCategories.entries())
          .map(([key, count]) => ({ key, label: CHAT_CAT_LABELS[key] || key, count }))
          .sort((a, b) => b.count - a.count),
        subcategories: Array.from(chatSubcategories.entries())
          .map(([subcategory, count]) => ({ subcategory, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
      };
      const todayChatStats = {
        opens: todayChatOpens,
        submits: todayChatSubmits,
        categories: Array.from(todayChatCategories.entries())
          .map(([key, count]) => ({ key, label: CHAT_CAT_LABELS[key] || key, count }))
          .sort((a, b) => b.count - a.count),
        subcategories: Array.from(todayChatSubcategories.entries())
          .map(([subcategory, count]) => ({ subcategory, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10),
      };

      // Statystyki ruchu wykluczonego (boty / Lighthouse / PageSpeed itp.)
      const patternCounts = new Map<string, { today: number; week: number }>();
      let botToday = 0;
      let botWeek = 0;
      (visits || []).forEach((r) => {
        // Pomijamy adminów i ruch z lovable - nie chcemy ich liczyć jako "boty"
        if (adminVisitorIds.has(r.visitor_id)) return;
        if (r.referrer && EXCLUDED_REFERRERS.some((ref) => r.referrer!.includes(ref))) return;
        // Bot: wpis ma flagę is_bot=true LUB UA pasuje do wzorca (wstecz kompatybilność).
        const flaggedBot = (r as { is_bot?: boolean }).is_bot === true;
        const label = matchBotPattern(r.user_agent || "") ?? (flaggedBot ? "Bot (flaga DB)" : null);
        if (!label) return;
        const d = new Date(r.created_at);
        const isToday = d >= todayStart;
        const isWeek = d >= weekAgo;
        if (isWeek) botWeek++;
        if (isToday) botToday++;
        const cur = patternCounts.get(label) ?? { today: 0, week: 0 };
        if (isWeek) cur.week++;
        if (isToday) cur.today++;
        patternCounts.set(label, cur);
      });
      const botStats = {
        today: botToday,
        week: botWeek,
        byPattern: Array.from(patternCounts.entries())
          .map(([pattern, v]) => ({ pattern, ...v }))
          .sort((a, b) => b.week - a.week),
      };

      // === Today-only conversion metrics (filter events by createdAt >= todayStart) ===
      const todayEvents = (events || []).filter((e: any) => new Date(e.created_at) >= todayStart);
      const todayShelterOpenEventsAll = todayEvents.filter((e: any) => e.event_name === "Otworzono schronisko");

      // Today shelter open sources
      const todaySourceCounts: Record<string, number> = {};
      todayShelterOpenEventsAll.forEach((e: any) => {
        const raw = e.event_data?.source || "unknown";
        const label = SOURCE_LABELS[raw] || raw;
        todaySourceCounts[label] = (todaySourceCounts[label] || 0) + 1;
      });
      const todayShelterOpenSources = Object.entries(todaySourceCounts)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      // Today contact clicks
      const todayContactCounts: Record<string, number> = {};
      todayEvents.forEach((e: any) => {
        if (contactTypes.includes(e.event_name)) {
          todayContactCounts[e.event_name] = (todayContactCounts[e.event_name] || 0) + 1;
        }
      });
      const todayContactClicks = contactTypes
        .map(type => ({ type, count: todayContactCounts[type] || 0 }))
        .filter(c => c.count > 0);

      // Today button clicks
      const todayClickCounts: Record<string, number> = {};
      todayEvents.forEach((e: any) => {
        if (e.event_name) {
          let label = e.event_name;
          if (e.event_name === "Filtr" && e.event_data?.filter) {
            label = `Filtr: ${e.event_data.filter} → ${e.event_data.enabled ? "ON" : "OFF"}`;
          } else if (e.event_name === "Filtr miasto" && e.event_data?.city) {
            label = `Filtr miasto: ${e.event_data.city}`;
          }
          todayClickCounts[label] = (todayClickCounts[label] || 0) + 1;
        }
      });
      const todayButtonClicks = Object.entries(todayClickCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      // Today city heatmap
      const todayCityCounts: Record<string, number> = {};
      todayShelterOpenEventsAll.forEach((e: any) => {
        const city = e.event_data?.shelter_city;
        const sid = e.event_data?.shelter_id;
        if (city) todayCityCounts[city] = (todayCityCounts[city] || 0) + 1;
        else if (sid && shelterMap.has(sid)) {
          const s = shelterMap.get(sid)!;
          todayCityCounts[s.city] = (todayCityCounts[s.city] || 0) + 1;
        }
      });
      const todayCityHeatmap = Object.entries(todayCityCounts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Today entry → exit (z dzisiejszych page_visits)
      const todayVisitorPages = new Map<string, { entry: string; exit: string }>();
      todayRows.forEach(r => {
        if (!todayVisitorPages.has(r.visitor_id)) {
          todayVisitorPages.set(r.visitor_id, { entry: r.path, exit: r.path });
        } else {
          todayVisitorPages.get(r.visitor_id)!.exit = r.path;
        }
      });
      const todayEntryExitCounts = new Map<string, number>();
      todayVisitorPages.forEach(({ entry, exit }) => {
        const key = `${entry}|||${exit}`;
        todayEntryExitCounts.set(key, (todayEntryExitCounts.get(key) || 0) + 1);
      });
      const todayEntryExitPages: EntryExitPage[] = Array.from(todayEntryExitCounts.entries())
        .map(([key, count]) => {
          const [entry, exit] = key.split("|||");
          return { entry, exit, count };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Today forms
      const todayFormOpens = todayEvents.filter((e: any) => e.event_name === "Formularz otwarty");
      const todayFormSubmits = todayEvents.filter((e: any) => e.event_name === "Formularz wysłany");
      const todaySeenOpens = new Map<string, any>();
      todayFormOpens.forEach((e: any) => {
        const key = dedupeKey(e);
        if (!todaySeenOpens.has(key)) todaySeenOpens.set(key, e);
      });
      const todayDedupedOpens = Array.from(todaySeenOpens.values());
      const todaySubmittedForms = todayFormSubmits.length;
      const todayAbandonedForms = Math.max(0, todayDedupedOpens.length - todaySubmittedForms);

      const todayFormShelterMap: Record<string, { name: string; opened: number; submitted: number }> = {};
      todayDedupedOpens.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        const key = sid || NEW_KEY;
        const name = sid
          ? (shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid))
          : "Nowe schronisko";
        if (!todayFormShelterMap[key]) todayFormShelterMap[key] = { name, opened: 0, submitted: 0 };
        todayFormShelterMap[key].opened++;
      });
      todayFormSubmits.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        const key = sid || NEW_KEY;
        const name = sid
          ? (shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid))
          : "Nowe schronisko";
        if (!todayFormShelterMap[key]) todayFormShelterMap[key] = { name, opened: 0, submitted: 0 };
        todayFormShelterMap[key].submitted++;
      });
      const todayFormsByShelter = Object.values(todayFormShelterMap)
        .sort((a, b) => (b.opened + b.submitted) - (a.opened + a.submitted))
        .slice(0, 10);

      const todaySubmittedKeys = new Set(todayFormSubmits.map((e: any) => dedupeKey(e)));
      const todayAbandonedFormsList = todayDedupedOpens
        .filter((e: any) => !todaySubmittedKeys.has(dedupeKey(e)))
        .map((e: any) => {
          const sid = e.event_data?.shelter_id;
          const name = sid && shelterMap.has(sid)
            ? shelterMap.get(sid)!.name
            : (e.event_data?.shelter_name || (e.event_data?.type === "nowe" ? "Nowe schronisko" : "—"));
          return { name, time: e.created_at, type: e.event_data?.type || "" };
        })
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 15);

      return {
        activeNow: activeNow ?? 0,
        todayVisitors,
        todayPageviews,
        yesterdayVisitors,
        yesterdayPageviews,
        weekVisitors,
        lastWeekVisitors,
        monthVisitors,
        lastMonthVisitors,
        weeklyData,
        weeklyTrend,
        monthlyTrend,
        topPages,
        topReferrers,
        deviceBreakdown,
        bounceRate,
        avgSessionDuration,
        
        peakHours,
        popularShelters,
        leastPopularShelters,
        entryExitPages,
        cityHeatmap,
        buttonClicks,
        recentlyOpenedShelters,
        shelterOpenSources,
        contactClicks,
        emptySearches,
        abandonedForms,
        submittedForms,
        formsByShelter,
        abandonedFormsList,
        todayAvgSessionDuration,
        todayReferrers,
        todayDeviceBreakdown,
        todayPopularShelters,
        todayEmptySearchesByVisitor,
        todayKeyPages,
        shareStats,
        chatStats,
        todayChatStats,
        botStats,
        todayShelterOpenSources,
        todayContactClicks,
        todayButtonClicks,
        todayCityHeatmap,
        todayEntryExitPages,
        todaySubmittedForms,
        todayAbandonedForms,
        todayFormsByShelter,
        todayAbandonedFormsList,
        emptySearchesByVisitor,
      };
    },
    refetchInterval: 60_000,
  });
};
